import { Box, Stack, Text } from '@mantine/core';
import { MediaBubble } from '../bubbles';
import { iconForButton, labelForUntitled } from '../../TemplatePreview';
import { ActionCard, BubbleBody, ButtonRows, type WaRendererProps } from './primitives';

/**
 * Rich template send. Header / body / footer / limited-time offer render
 * inline in the normal bubble chrome (same as every other message type);
 * buttons and carousel cards render as separate action cards below, matching
 * the interactive-button and flow-CTA cards rather than the authoring-only
 * `<TemplatePreview>` phone mockup.
 *
 * Legacy rows and AUTHENTICATION templates have no snapshot — `view.template`
 * is null there and the plain body text (already the true rendered text) shows.
 */
export function TemplateMessage({ view, incoming }: WaRendererProps) {
  const t = view.template;
  if (!t) return <BubbleBody>{view.body ?? view.fallbackLabel}</BubbleBody>;
  const header = t.header;
  return (
    <Stack gap={4}>
      {header?.format === 'TEXT' && header.text && (
        <Text size="sm" fw={700} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {header.text}
        </Text>
      )}
      {(header?.format === 'IMAGE' || header?.format === 'VIDEO' || header?.format === 'GIF') && (
        <MediaBubble
          kind={header.format === 'IMAGE' ? 'image' : 'video'}
          url={header.mediaPreviewUrl}
          alt="template header"
        />
      )}
      {header?.format === 'DOCUMENT' && (
        <MediaBubble kind="file" url={header.mediaPreviewUrl} alt="template header" />
      )}
      {header?.format === 'LOCATION' && <Text size="sm">📍 Shared location</Text>}
      <BubbleBody>{view.body ?? t.bodyText}</BubbleBody>
      {t.footerText && (
        <Text
          size="xs"
          c={incoming ? 'dimmed' : 'rgba(255,255,255,0.7)'}
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {t.footerText}
        </Text>
      )}
      {t.limitedTimeOffer?.text && (
        <Box
          p={6}
          style={{
            borderRadius: 6,
            background: incoming
              ? 'light-dark(var(--mantine-color-orange-0), var(--mantine-color-dark-5))'
              : 'rgba(255,255,255,0.15)',
          }}
        >
          <Text size="xs" fw={600}>
            🎁 {t.limitedTimeOffer.text}
          </Text>
          {t.limitedTimeOffer.hasExpiration && (
            <Text size="xs" c={incoming ? 'dimmed' : 'rgba(255,255,255,0.85)'}>
              Offer ends soon
            </Text>
          )}
        </Box>
      )}
    </Stack>
  );
}

export function TemplateCard({ view }: WaRendererProps) {
  const t = view.template;
  if (!t) return null;

  if (t.carouselCards && t.carouselCards.length > 0) {
    return (
      <Stack gap={4}>
        {t.carouselCards.map((card, i) => (
          <ActionCardPadded key={i}>
            <Stack gap={4}>
              <MediaBubble
                kind={card.headerFormat === 'IMAGE' ? 'image' : 'video'}
                url={card.mediaPreviewUrl}
                alt={`carousel card ${i + 1}`}
              />
              {card.bodyText && <Text size="sm">{card.bodyText}</Text>}
              {card.buttons && card.buttons.length > 0 && (
                <Box
                  style={{
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid var(--mantine-color-default-border)',
                  }}
                >
                  <ButtonRows
                    buttons={card.buttons.map((b, bi) => ({
                      key: `${b.type}-${bi}`,
                      icon: iconForButton(b.type),
                      label: b.text || labelForUntitled(b.type),
                    }))}
                  />
                </Box>
              )}
            </Stack>
          </ActionCardPadded>
        ))}
      </Stack>
    );
  }

  if (!t.buttons || t.buttons.length === 0) return null;
  return (
    <ActionCard>
      <ButtonRows
        buttons={t.buttons.map((b, i) => ({
          key: `${b.type}-${i}`,
          icon: iconForButton(b.type),
          label: b.text || labelForUntitled(b.type),
        }))}
      />
    </ActionCard>
  );
}

/** Carousel cards need inner padding; the button card is full-bleed. */
function ActionCardPadded({ children }: { children: React.ReactNode }) {
  return (
    <Box
      style={{
        background: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))',
        border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
        color: 'light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-0))',
        borderRadius: 14,
        overflow: 'hidden',
        padding: 'var(--mantine-spacing-xs)',
      }}
    >
      {children}
    </Box>
  );
}
